import {
	countyLabel,
	countySlugSchema,
	REGION_NAME,
} from "@nwga/contracts/region";
import { JobFilters, JobList } from "@nwga/features/jobs/ui";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { Container } from "../../components/container.tsx";
import { HOME_CRUMB, PageHero } from "../../components/page-hero.tsx";

/**
 * The job board.
 *
 * Filter state lives entirely in the URL. That is not a stylistic choice: a
 * filtered board has to be a shareable link, the back button has to undo a
 * filter, and a reload has to land on the same results. Keeping it in component
 * state would break all three.
 */
const searchSchema = z.object({
	q: z.string().optional(),
	county: countySlugSchema.optional(),
	employmentType: z
		.array(
			z.enum([
				"full_time",
				"part_time",
				"contract",
				"temporary",
				"internship",
				"seasonal",
			]),
		)
		.optional(),
	workArrangement: z.array(z.enum(["onsite", "hybrid", "remote"])).optional(),
});

export const Route = createFileRoute("/jobs/")({
	validateSearch: searchSchema,
	component: JobsPage,
});

function JobsPage() {
	const search = Route.useSearch();
	const navigate = useNavigate({ from: Route.fullPath });

	const filters = {
		...search,
		sort: "published_at" as const,
		order: "desc" as const,
	};

	// The hero names the filter that is on, so a shared link explains itself.
	const title =
		search.county === undefined
			? `Jobs in ${REGION_NAME}`
			: `Jobs in ${countyLabel(search.county)}`;

	return (
		<>
			<PageHero
				title={title}
				breadcrumbs={
					search.county === undefined
						? [HOME_CRUMB, { label: "Jobs" }]
						: [
								HOME_CRUMB,
								{ label: "Jobs", to: "/jobs" },
								{ label: countyLabel(search.county) },
							]
				}
			/>

			<Container className="py-12">
				<div className="grid items-start gap-10 lg:grid-cols-[16rem_1fr] lg:gap-14">
					{/*
            The rail follows the list down the page.

            Sticky only from `lg`, where it is a column beside the results. On a
            narrow screen it is stacked above them, and pinning it there would
            spend a third of a phone screen on filters somebody has already set.

            `items-start` on the grid is what makes it work at all: grid items
            stretch to the row's height by default, and an item already as tall
            as its row has no travel to stick through.

            The rail can outgrow a short window — a laptop at 720px has about
            600px to give it — so it scrolls internally past that point. The
            negative margin and matching padding keep that scroll box from
            clipping the focus ring on the chips at its edges.
          */}
					<aside className="lg:sticky lg:top-[calc(var(--header-height)+1.5rem)] lg:-mx-2 lg:max-h-[calc(100dvh-var(--header-height)-3rem)] lg:overflow-y-auto lg:px-2">
						<JobFilters
							value={filters}
							onChange={(next, options) =>
								void navigate({
									search: {
										...(next.q === undefined ? {} : { q: next.q }),
										...(next.county === undefined
											? {}
											: { county: next.county }),
										...(next.employmentType === undefined
											? {}
											: { employmentType: next.employmentType }),
										...(next.workArrangement === undefined
											? {}
											: { workArrangement: next.workArrangement }),
									},
									// Only the debounced search box asks to replace; see
									// JobFilterChangeOptions.
									replace: options?.replace ?? false,
								})
							}
						/>
					</aside>

					<JobList filters={filters} />
				</div>
			</Container>
		</>
	);
}
